import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useAuthContext } from "../contexts/AuthContext";
import { authenticatedFetch } from "../utils/authenticatedFetch";
import styles from "./AdminAnnouncements.module.css";

const apiUrl = process.env.REACT_APP_API_URL || "http://localhost:5001";

type AnnouncementStatus = "draft" | "published" | "archived";
type AnnouncementVariant = "info" | "warning" | "success" | "danger" | "promo";

interface Announcement {
  id: string;
  status: AnnouncementStatus;
  variant: AnnouncementVariant;
  start_at: string;
  end_at?: string | null;
  priority: number;
  is_dismissible: boolean;
  sticky: boolean;
  locations: string[];
  title_translations: Record<string, string>;
  body_translations: Record<string, string>;
  cta?: {
    text_translations?: Record<string, string>;
    url?: string | null;
    track?: string | null;
  } | null;
  target_rules?: Record<string, unknown> | null;
  created_at: string;
  updated_at: string;
}

interface FormState {
  id: string | null;
  status: AnnouncementStatus;
  variant: AnnouncementVariant;
  priority: string;
  startAt: string;
  endAt: string;
  isDismissible: boolean;
  sticky: boolean;
  locations: string[];
  titleEn: string;
  titleAr: string;
  bodyEn: string;
  bodyAr: string;
  ctaTextEn: string;
  ctaTextAr: string;
  ctaUrl: string;
  ctaTrack: string;
  targetRules: string;
}

const LOCATION_OPTIONS = ["global", "home", "pricing", "chat", "qbank"];

const defaultForm = (): FormState => ({
  id: null,
  status: "draft",
  variant: "info",
  priority: "100",
  startAt: "",
  endAt: "",
  isDismissible: true,
  sticky: true,
  locations: ["global"],
  titleEn: "",
  titleAr: "",
  bodyEn: "",
  bodyAr: "",
  ctaTextEn: "",
  ctaTextAr: "",
  ctaUrl: "",
  ctaTrack: "",
  targetRules: "",
});

const toDatetimeInput = (value?: string | null) => {
  if (!value) return "";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "";
  const localDate = new Date(date.getTime() - date.getTimezoneOffset() * 60000);
  return localDate.toISOString().slice(0, 16);
};

const toApiDatetime = (value: string) => {
  if (!value) return null;
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  return date.toISOString();
};

const formatDate = (value?: string | null) => {
  if (!value) return "No date";
  try {
    return new Date(value).toLocaleString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
      hour: "numeric",
      minute: "2-digit",
    });
  } catch {
    return value;
  }
};

const notify = (message: string, type: "success" | "error" | "warning" = "success") => {
  (window as any).showNotification?.(message, type);
};

const readApiError = (payload: any, fallback: string) =>
  payload?.error || payload?.message || fallback;

const AdminAnnouncements: React.FC = () => {
  const { user } = useAuthContext();
  const [announcements, setAnnouncements] = useState<Announcement[]>([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [actionLoading, setActionLoading] = useState<string | null>(null);
  const [statusFilter, setStatusFilter] = useState("all");
  const [locationFilter, setLocationFilter] = useState("all");
  const [query, setQuery] = useState("");
  const [form, setForm] = useState<FormState>(defaultForm());

  const fetchAnnouncements = useCallback(async () => {
    if (!user?.id) return;
    setLoading(true);
    try {
      const params = new URLSearchParams({
        user_id: user.id,
        status: statusFilter,
        location: locationFilter,
        query,
      });
      const res = await authenticatedFetch(`${apiUrl}/api/admin/announcements?${params}`);
      const data = await res.json();
      if (!res.ok) throw new Error(readApiError(data, "Failed to load announcements"));
      setAnnouncements(Array.isArray(data) ? data : []);
    } catch (error: any) {
      notify(error.message || "Failed to load announcements", "error");
    } finally {
      setLoading(false);
    }
  }, [user?.id, statusFilter, locationFilter, query]);

  useEffect(() => {
    fetchAnnouncements();
  }, [fetchAnnouncements]);

  const selectedAnnouncement = useMemo(
    () => announcements.find((announcement) => announcement.id === form.id) || null,
    [announcements, form.id]
  );

  const editAnnouncement = (announcement: Announcement) => {
    setForm({
      id: announcement.id,
      status: announcement.status,
      variant: announcement.variant,
      priority: String(announcement.priority ?? 100),
      startAt: toDatetimeInput(announcement.start_at),
      endAt: toDatetimeInput(announcement.end_at),
      isDismissible: announcement.is_dismissible,
      sticky: announcement.sticky,
      locations: announcement.locations || ["global"],
      titleEn: announcement.title_translations?.en || "",
      titleAr: announcement.title_translations?.ar || "",
      bodyEn: announcement.body_translations?.en || "",
      bodyAr: announcement.body_translations?.ar || "",
      ctaTextEn: announcement.cta?.text_translations?.en || "",
      ctaTextAr: announcement.cta?.text_translations?.ar || "",
      ctaUrl: announcement.cta?.url || "",
      ctaTrack: announcement.cta?.track || "",
      targetRules: announcement.target_rules
        ? JSON.stringify(announcement.target_rules, null, 2)
        : "",
    });
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const buildPayload = (statusOverride?: AnnouncementStatus) => {
    const hasCta = form.ctaTextEn.trim() || form.ctaTextAr.trim() || form.ctaUrl.trim();
    return {
      admin_id: user?.id,
      status: statusOverride || form.status,
      variant: form.variant,
      priority: form.priority,
      start_at: toApiDatetime(form.startAt),
      end_at: toApiDatetime(form.endAt),
      is_dismissible: form.isDismissible,
      sticky: form.sticky,
      locations: form.locations,
      title_translations: { en: form.titleEn, ar: form.titleAr },
      body_translations: { en: form.bodyEn, ar: form.bodyAr },
      cta: hasCta
        ? {
            text_translations: { en: form.ctaTextEn, ar: form.ctaTextAr },
            url: form.ctaUrl,
            track: form.ctaTrack,
          }
        : null,
      target_rules: form.targetRules || null,
    };
  };

  const saveAnnouncement = async (statusOverride?: AnnouncementStatus) => {
    if (!user?.id) return;
    setSaving(true);
    try {
      const endpoint = form.id
        ? `${apiUrl}/api/admin/announcements/${form.id}`
        : `${apiUrl}/api/admin/announcements`;
      const method = form.id ? "PUT" : "POST";
      const res = await authenticatedFetch(endpoint, {
        method,
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(buildPayload(statusOverride)),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(readApiError(data, "Failed to save announcement"));

      await fetchAnnouncements();
      setForm(defaultForm());
      notify(statusOverride === "published" ? "Announcement published" : "Announcement saved", "success");
    } catch (error: any) {
      notify(error.message || "Failed to save announcement", "error");
    } finally {
      setSaving(false);
    }
  };

  const archiveAnnouncement = async (announcement: Announcement) => {
    if (!user?.id) return;
    setActionLoading(announcement.id);
    try {
      const res = await authenticatedFetch(
        `${apiUrl}/api/admin/announcements/${announcement.id}/archive`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ admin_id: user.id }),
        }
      );
      const data = await res.json();
      if (!res.ok) throw new Error(readApiError(data, "Failed to archive announcement"));
      await fetchAnnouncements();
      notify("Announcement archived", "success");
    } catch (error: any) {
      notify(error.message || "Failed to archive announcement", "error");
    } finally {
      setActionLoading(null);
    }
  };

  const toggleLocation = (location: string) => {
    setForm((current) => {
      if (current.locations.includes(location)) {
        const next = current.locations.filter((item) => item !== location);
        return { ...current, locations: next.length ? next : ["global"] };
      }
      return { ...current, locations: [...current.locations, location] };
    });
  };

  return (
    <div className={styles.container}>
      <div className={styles.header}>
        <div>
          <h1>Announcements</h1>
          <p>Create banners for global, pricing, chat, home, and QBank locations.</p>
        </div>
        <button className={styles.secondaryButton} onClick={() => setForm(defaultForm())}>
          New Announcement
        </button>
      </div>

      <section className={styles.panel}>
        <div className={styles.panelHeader}>
          <h2>{form.id ? `Edit ${selectedAnnouncement?.title_translations?.en || "announcement"}` : "Create announcement"}</h2>
        </div>

        <div className={styles.formGrid}>
          <label className={styles.field}>
            <span>Status</span>
            <select
              className={styles.select}
              value={form.status}
              onChange={(event) => setForm({ ...form, status: event.target.value as AnnouncementStatus })}
            >
              <option value="draft">Draft</option>
              <option value="published">Published</option>
              <option value="archived">Archived</option>
            </select>
          </label>
          <label className={styles.field}>
            <span>Variant</span>
            <select
              className={styles.select}
              value={form.variant}
              onChange={(event) => setForm({ ...form, variant: event.target.value as AnnouncementVariant })}
            >
              <option value="info">Info</option>
              <option value="promo">Promo</option>
              <option value="success">Success</option>
              <option value="warning">Warning</option>
              <option value="danger">Danger</option>
            </select>
          </label>
          <label className={styles.field}>
            <span>Priority</span>
            <input
              className={styles.input}
              value={form.priority}
              onChange={(event) => setForm({ ...form, priority: event.target.value })}
              inputMode="numeric"
            />
          </label>
          <label className={styles.field}>
            <span>Starts at</span>
            <input
              type="datetime-local"
              className={styles.input}
              value={form.startAt}
              onChange={(event) => setForm({ ...form, startAt: event.target.value })}
            />
          </label>
          <label className={styles.field}>
            <span>Ends at</span>
            <input
              type="datetime-local"
              className={styles.input}
              value={form.endAt}
              onChange={(event) => setForm({ ...form, endAt: event.target.value })}
            />
          </label>
          <label className={styles.toggleField}>
            <input
              type="checkbox"
              checked={form.isDismissible}
              onChange={(event) => setForm({ ...form, isDismissible: event.target.checked })}
            />
            Dismissible
          </label>
          <label className={styles.toggleField}>
            <input
              type="checkbox"
              checked={form.sticky}
              onChange={(event) => setForm({ ...form, sticky: event.target.checked })}
            />
            Sticky
          </label>
        </div>

        <div className={styles.locationGroup}>
          <span>Locations</span>
          <div className={styles.locationOptions}>
            {LOCATION_OPTIONS.map((location) => (
              <label key={location}>
                <input
                  type="checkbox"
                  checked={form.locations.includes(location)}
                  onChange={() => toggleLocation(location)}
                />
                {location}
              </label>
            ))}
          </div>
        </div>

        <div className={styles.formGrid}>
          <label className={styles.field}>
            <span>English title</span>
            <input
              className={styles.input}
              value={form.titleEn}
              onChange={(event) => setForm({ ...form, titleEn: event.target.value })}
            />
          </label>
          <label className={styles.field}>
            <span>Arabic title</span>
            <input
              className={styles.input}
              value={form.titleAr}
              onChange={(event) => setForm({ ...form, titleAr: event.target.value })}
            />
          </label>
          <label className={styles.field}>
            <span>English body</span>
            <textarea
              className={styles.textarea}
              value={form.bodyEn}
              onChange={(event) => setForm({ ...form, bodyEn: event.target.value })}
              rows={3}
            />
          </label>
          <label className={styles.field}>
            <span>Arabic body</span>
            <textarea
              className={styles.textarea}
              value={form.bodyAr}
              onChange={(event) => setForm({ ...form, bodyAr: event.target.value })}
              rows={3}
            />
          </label>
        </div>

        <div className={styles.formGrid}>
          <label className={styles.field}>
            <span>CTA English text</span>
            <input
              className={styles.input}
              value={form.ctaTextEn}
              onChange={(event) => setForm({ ...form, ctaTextEn: event.target.value })}
            />
          </label>
          <label className={styles.field}>
            <span>CTA Arabic text</span>
            <input
              className={styles.input}
              value={form.ctaTextAr}
              onChange={(event) => setForm({ ...form, ctaTextAr: event.target.value })}
            />
          </label>
          <label className={styles.field}>
            <span>CTA URL</span>
            <input
              className={styles.input}
              value={form.ctaUrl}
              onChange={(event) => setForm({ ...form, ctaUrl: event.target.value })}
              placeholder="/pricing?coupon=CODE"
            />
          </label>
          <label className={styles.field}>
            <span>CTA tracking key</span>
            <input
              className={styles.input}
              value={form.ctaTrack}
              onChange={(event) => setForm({ ...form, ctaTrack: event.target.value })}
            />
          </label>
        </div>

        <label className={styles.field}>
          <span>Target rules JSON</span>
          <textarea
            className={styles.textarea}
            value={form.targetRules}
            onChange={(event) => setForm({ ...form, targetRules: event.target.value })}
            rows={3}
            placeholder="Optional"
          />
        </label>

        <div className={`${styles.preview} ${styles[form.variant]}`}>
          <strong>{form.titleEn || "Announcement title"}</strong>
          <span>{form.bodyEn || "Announcement body preview"}</span>
          {form.ctaTextEn && form.ctaUrl && <a href={form.ctaUrl}>{form.ctaTextEn}</a>}
        </div>

        <div className={styles.actionsRow}>
          <button
            className={styles.primaryButton}
            onClick={() => saveAnnouncement()}
            disabled={saving}
          >
            {saving ? "Saving..." : form.id ? "Update announcement" : "Save announcement"}
          </button>
          <button
            className={styles.secondaryButton}
            onClick={() => saveAnnouncement("draft")}
            disabled={saving}
          >
            Save draft
          </button>
          <button
            className={styles.secondaryButton}
            onClick={() => saveAnnouncement("published")}
            disabled={saving}
          >
            Publish
          </button>
        </div>
      </section>

      <section className={styles.panel}>
        <div className={styles.panelHeader}>
          <h2>Announcement library</h2>
          <button className={styles.secondaryButton} onClick={fetchAnnouncements} disabled={loading}>
            Refresh
          </button>
        </div>

        <div className={styles.filters}>
          <input
            className={styles.input}
            value={query}
            onChange={(event) => setQuery(event.target.value)}
            placeholder="Search title or body"
          />
          <select
            className={styles.select}
            value={statusFilter}
            onChange={(event) => setStatusFilter(event.target.value)}
          >
            <option value="all">All statuses</option>
            <option value="draft">Draft</option>
            <option value="published">Published</option>
            <option value="archived">Archived</option>
          </select>
          <select
            className={styles.select}
            value={locationFilter}
            onChange={(event) => setLocationFilter(event.target.value)}
          >
            <option value="all">All locations</option>
            {LOCATION_OPTIONS.map((location) => (
              <option key={location} value={location}>
                {location}
              </option>
            ))}
          </select>
        </div>

        {loading ? (
          <div className={styles.emptyState}>Loading announcements...</div>
        ) : announcements.length === 0 ? (
          <div className={styles.emptyState}>No announcements match these filters.</div>
        ) : (
          <div className={styles.tableWrap}>
            <table className={styles.table}>
              <thead>
                <tr>
                  <th>Title</th>
                  <th>Status</th>
                  <th>Variant</th>
                  <th>Locations</th>
                  <th>Priority</th>
                  <th>Window</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {announcements.map((announcement) => (
                  <tr key={announcement.id}>
                    <td>
                      <strong>{announcement.title_translations?.en || "Untitled"}</strong>
                      <div className={styles.helpText}>{announcement.body_translations?.en || ""}</div>
                    </td>
                    <td>
                      <span className={`${styles.badge} ${styles[announcement.status]}`}>
                        {announcement.status}
                      </span>
                    </td>
                    <td>{announcement.variant}</td>
                    <td>{(announcement.locations || []).join(", ")}</td>
                    <td>{announcement.priority}</td>
                    <td>
                      <div>{formatDate(announcement.start_at)}</div>
                      <div className={styles.helpText}>{formatDate(announcement.end_at)}</div>
                    </td>
                    <td>
                      <div className={styles.tableActions}>
                        <button className={styles.linkButton} onClick={() => editAnnouncement(announcement)}>
                          Edit
                        </button>
                        {announcement.status !== "archived" && (
                          <button
                            className={styles.linkButtonDanger}
                            disabled={actionLoading === announcement.id}
                            onClick={() => archiveAnnouncement(announcement)}
                          >
                            {actionLoading === announcement.id ? "..." : "Archive"}
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>
    </div>
  );
};

export default AdminAnnouncements;
